import is from '@sindresorhus/is';
import { motion } from 'framer-motion';
import { useEffect, useRef, useState } from 'react';

import { Button } from '../../core/button/Button';
import { Card } from '../../core/card/Card';
import { IconAsterisk, IconChevronDownSmall, IconCircleCheck, IconCircleX } from '../../core/icons';
import { ScrollArea } from '../../core/scroll-area/ScrollArea';
import { Spinner } from '../../core/spinner/Spinner';

export type ActivityTimelineStepStatus = 'active' | 'complete' | 'error' | 'pending';

export type ActivityTimelineStep = {
    readonly detail?: string;
    readonly id: string;
    readonly label: string;
    readonly status: ActivityTimelineStepStatus;
};

export type ActivityTimelineUsage = {
    readonly credits?: number;
    readonly durationMs?: number;
    readonly tokens?: number;
    readonly toolCalls?: number;
};

export type ActivityTimelineCardProps = {
    readonly autoCollapseWhenComplete?: boolean;
    readonly defaultCollapsed?: boolean;
    readonly doneLabel?: string;
    readonly isClosed?: boolean;
    readonly isTurnComplete?: boolean;
    readonly steps: readonly ActivityTimelineStep[];
    readonly title: string;
    readonly usage?: ActivityTimelineUsage;
};

const formatDuration = (durationMs: number): string => {
    const totalSeconds = Math.max(1, Math.round(durationMs / 1000));
    if (totalSeconds < 60) return `${totalSeconds}s`;
    const minutes = Math.floor(totalSeconds / 60);
    const seconds = totalSeconds % 60;
    return seconds === 0 ? `${minutes}m` : `${minutes}m ${seconds}s`;
};

const hasOpenStep = (steps: readonly ActivityTimelineStep[]): boolean =>
    steps.some((step) => step.status === 'active' || step.status === 'pending');

const renderStatusIcon = (status: ActivityTimelineStepStatus) => {
    if (status === 'complete') return <IconCircleCheck size={12} className="text-black-900" aria-hidden={true} />;
    if (status === 'error') return <IconCircleX size={12} className="text-error-700" aria-hidden={true} />;
    if (status === 'active') return <Spinner className="mr-0! text-black-900" />;
    return <Spinner className="mr-0! text-gray-500" />;
};

const DONE_ENTRY_KEY = '__done__';
const ENTER_INITIAL = { opacity: 0.5, scale: 0.97, x: 'calc(-1 * var(--spacing-l))' } as const;
const ENTER_ANIMATE = { opacity: 1, scale: 1, x: '0%' } as const;
const ENTER_TRANSITION = { duration: 0.2, ease: 'easeOut' } as const;

export function ActivityTimelineCard({
    autoCollapseWhenComplete = false,
    defaultCollapsed,
    doneLabel = 'Done',
    isClosed = false,
    isTurnComplete = true,
    steps,
    title,
    usage,
}: ActivityTimelineCardProps) {
    const areStepsComplete = !hasOpenStep(steps);
    // The "Done" bullet marks the real end of the run; a card closed by an interruption (e.g. the
    // agent asked a question and continues in a new card below it) settles without it.
    const isComplete = areStepsComplete && isTurnComplete;
    const isSettled = isComplete || isClosed;
    const durationMs = usage?.durationMs;
    const [runningDurationMs, setRunningDurationMs] = useState(durationMs ?? 0);
    const durationSummary =
        !areStepsComplete || !is.undefined(durationMs) || runningDurationMs > 0
            ? formatDuration(runningDurationMs)
            : null;
    const [isCollapsed, setIsCollapsed] = useState(defaultCollapsed ?? (autoCollapseWhenComplete && isSettled));
    const wasSettledRef = useRef(isSettled);

    // Animate each entry only the first time it appears, not on every collapse/expand remount.
    const animatedEntriesRef = useRef<Set<string>>(new Set());
    const shouldAnimateEntry = (key: string): boolean => !animatedEntriesRef.current.has(key);
    useEffect(() => {
        for (const step of steps) animatedEntriesRef.current.add(step.id);
        if (isComplete) animatedEntriesRef.current.add(DONE_ENTRY_KEY);
    });

    // The step list is capped in height; keep the newest step in view as the run progresses.
    const itemsRef = useRef<HTMLDivElement>(null);
    // biome-ignore lint/correctness/useExhaustiveDependencies: re-pins the scroll position when a step is added or the list remounts after expanding.
    useEffect(() => {
        const node = itemsRef.current;
        if (is.null(node)) return;
        const scrollToBottom = () => {
            node.scrollTop = node.scrollHeight;
        };
        scrollToBottom();
        const animationFrame = window.requestAnimationFrame(scrollToBottom);
        return () => window.cancelAnimationFrame(animationFrame);
    }, [steps.length, isCollapsed, isComplete]);

    // The timer covers the agent run only: it starts with the first step and freezes once the last
    // step is done, even while the turn keeps streaming. Display is monotonic — the locally accrued
    // time and the server-reported duration can disagree (event latency), so never jump backwards.
    useEffect(() => {
        const baseDurationMs = durationMs ?? 0;
        setRunningDurationMs((current) => Math.max(current, baseDurationMs));
        if (areStepsComplete) return;

        const startedAt = Date.now();
        const interval = window.setInterval(() => {
            setRunningDurationMs((current) => Math.max(current, baseDurationMs + Date.now() - startedAt));
        }, 1000);
        return () => window.clearInterval(interval);
    }, [durationMs, areStepsComplete]);

    useEffect(() => {
        if (autoCollapseWhenComplete && isSettled && !wasSettledRef.current) {
            setIsCollapsed(true);
        }
        wasSettledRef.current = isSettled;
    }, [autoCollapseWhenComplete, isSettled]);

    return (
        <Card className="p-l! pl-xs! shadow-none!">
            <div className="flex items-center gap-l pl-xxs">
                <span className="flex shrink-0 items-center justify-center text-orange-500">
                    <IconAsterisk size={16} aria-hidden={true} />
                </span>
                <div className="flex min-w-0 flex-wrap items-center gap-x-xs gap-y-0">
                    <p className="m-0 text-sm leading-5 text-black-900">{title}</p>
                    {!is.null(durationSummary) ? (
                        <div className="flex flex-wrap items-center gap-xs">
                            <p className="m-0 text-xxs font-normal leading-4 text-gray-600">Worked for</p>
                            <span className="text-xxs leading-4 text-gray-700">{durationSummary}</span>
                        </div>
                    ) : null}
                    <Button
                        variant="link"
                        iconOnly={true}
                        className="size-4! p-0! [&>svg]:h-4! [&>svg]:w-4!"
                        aria-label={isCollapsed ? 'Expand activity trace' : 'Collapse activity trace'}
                        onClick={() => setIsCollapsed((current) => !current)}
                    >
                        <IconChevronDownSmall
                            size={16}
                            aria-hidden={true}
                            className="transition-transform duration-200 ease"
                            style={{ transform: isCollapsed ? 'rotate(-90deg)' : 'rotate(0deg)' }}
                        />
                    </Button>
                </div>
            </div>
            {!isCollapsed ? (
                <div className="mt-m pl-xs">
                    <ScrollArea
                        className="h-[8rem]!"
                        orientation="vertical"
                        viewportClassName="flex flex-col gap-xs pb-xxs"
                        viewportRef={itemsRef}
                    >
                        <div className="flex flex-col gap-xs">
                            {steps.map((step) => (
                                <motion.div
                                    key={step.id}
                                    className="relative flex flex-row gap-xl"
                                    initial={shouldAnimateEntry(step.id) ? ENTER_INITIAL : false}
                                    animate={ENTER_ANIMATE}
                                    transition={ENTER_TRANSITION}
                                >
                                    <span className="z-10 flex h-3xl w-xl shrink-0 items-center justify-center bg-white-200">
                                        {renderStatusIcon(step.status)}
                                    </span>
                                    <span className="text-sm leading-5 text-black-700">{step.label}</span>
                                </motion.div>
                            ))}
                            {isComplete ? (
                                <motion.div
                                    className="relative flex flex-row gap-xl"
                                    initial={shouldAnimateEntry(DONE_ENTRY_KEY) ? ENTER_INITIAL : false}
                                    animate={ENTER_ANIMATE}
                                    transition={ENTER_TRANSITION}
                                >
                                    <span className="z-10 flex h-3xl w-xl shrink-0 items-center justify-center bg-white-200">
                                        {renderStatusIcon('complete')}
                                    </span>
                                    <span className="text-sm leading-5 text-black-700">{doneLabel}</span>
                                </motion.div>
                            ) : null}
                        </div>
                    </ScrollArea>
                </div>
            ) : null}
        </Card>
    );
}
